import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { BrandedEmptyState, TableSkeleton, toast } from '../../components/index.js';
import {
  FEEDBACK_INTAKE_TYPES,
  MY_FEEDBACK_STATES,
  feedbackEntryTypePluralLabel,
  rbac,
} from '@dha-nexus/shared';
import { useCurrentUser } from '../../app/CurrentUserContext.jsx';
import {
  commentOnGovernedFeedback,
  FEEDBACK_COMMENT_MAX_LENGTH,
  getFeedbackPackage,
  getGovernedFeedback,
  listGovernedFeedback,
} from './feedbackApi.js';
import {
  clearPendingFeedbackMutation,
  feedbackMutationSignature,
  pendingFeedbackMutation,
  shouldRetainFeedbackMutation,
} from './feedbackIdempotency.js';
import { MySupportTicketsSection } from '../feedbackIntake/MySupportTicketsSection.jsx';
import { useFeedbackMarkup } from './FeedbackMarkupProvider.jsx';
import { recordChip } from './governedRecordChip.js';
import { countFeedbackScreens } from './feedbackSessionCounts.js';
import { FeedbackThumbStrip } from './FeedbackThumbStrip.jsx';
import './feedbackMarkup.redesign.css';

const TERMINAL_RECORD_STATUSES = new Set(['resolved', 'rejected', 'duplicate']);
const LIFECYCLE_VIEWS = ['drafts', 'submitted', 'resolved'];
const VALID_VIEWS = new Set(LIFECYCLE_VIEWS);
const VIEW_TO_LIFECYCLE = Object.freeze({
  drafts: 'draft',
  submitted: 'submitted',
  resolved: 'resolved',
});
const EMPTY_LIFECYCLE_COUNTS = Object.freeze({ draft: 0, submitted: 0, resolved: 0 });

// -------------------------------------------------------------------------
// Unified type/state filters (commit 6)
// -------------------------------------------------------------------------
//
// One page over TWO sources: screenshot packages (the lifecycle tabs below) and
// typed intakes (the section at the bottom). The filters coordinate the two
// without changing what either one means:
//
//   - TYPE selects which sources are shown at all. 'screen_feedback' is a source,
//     not an intake type, so it hides the typed section rather than filtering it.
//   - STATE is shared vocabulary. For packages it drives the existing lifecycle
//     tab (the `view` search param, unchanged); for intakes it is derived from
//     the intake status plus the linked record's status.
//
// Both default to "everything", so a page load with no filters behaves exactly as
// it did before this commit — which is what keeps the package tab semantics, the
// default-view heuristics, and the existing package tests intact.
const TYPE_FILTER_ALL = '';
const SCREEN_FEEDBACK_TYPE = 'screen_feedback';
const TYPE_FILTER_OPTIONS = [
  { value: TYPE_FILTER_ALL, label: 'All feedback' },
  { value: SCREEN_FEEDBACK_TYPE, label: feedbackEntryTypePluralLabel(SCREEN_FEEDBACK_TYPE) },
  ...FEEDBACK_INTAKE_TYPES.map((intakeType) => ({
    value: intakeType,
    label: feedbackEntryTypePluralLabel(intakeType),
  })),
];

const STATE_FILTER_LABELS = Object.freeze({
  drafts: 'Drafts',
  submitted: 'Submitted',
  resolved: 'Resolved',
  deflected: 'Answered by PRIME',
});
const STATE_FILTER_OPTIONS = [
  { value: '', label: 'Any state' },
  ...MY_FEEDBACK_STATES.map((state) => ({ value: state, label: STATE_FILTER_LABELS[state] })),
];

// Screenshot packages have no `deflected` state — only PRIME answering a question
// produces one — so that state means "typed intakes only".
const PACKAGE_STATES = Object.freeze(['drafts', 'submitted', 'resolved']);
const STATE_TO_VIEW = Object.freeze({
  drafts: 'drafts',
  submitted: 'submitted',
  resolved: 'resolved',
});

function formatWhen(value) {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  // Older records need the year — "Dec 3, 4:12 PM" is ambiguous months later.
  const sameYear = date.getFullYear() === new Date().getFullYear();
  return date.toLocaleString(undefined, {
    ...(sameYear ? {} : { year: 'numeric' }),
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

// Re-exported from the leaf module so the typed-intake section can share the
// vocabulary without importing this page (which imports that section).
export { recordChip };

export function packageTimelineStage(records) {
  if (!Array.isArray(records) || records.length === 0) return 0;
  if (records.every((record) => TERMINAL_RECORD_STATUSES.has(record.status))) return 2;
  if (records.some((record) => record.status && record.status !== 'new')) return 1;
  return 0;
}

export function packageTimelineStageFromSummary(summary, records = []) {
  if (!summary) return packageTimelineStage(records);
  const expectedCount = Number(summary.expectedCount ?? summary.expected ?? 0);
  const linkedCount = Number(summary.linkedCount ?? summary.total ?? 0);
  const terminalCount = Number(summary.terminalCount ?? summary.terminal ?? 0);
  const statusCounts = summary.statusCounts || summary.byStatus || {};
  const integrityHealthy =
    summary.integrityStatus === 'healthy' ||
    (summary.integrityStatus == null && expectedCount === linkedCount);
  if (integrityHealthy && linkedCount > 0 && terminalCount === linkedCount) return 2;
  const reviewedCount = Object.entries(statusCounts).reduce(
    (count, [status, value]) => count + (status === 'new' ? 0 : Number(value || 0)),
    0,
  );
  return reviewedCount > 0 ? 1 : 0;
}

const TIMELINE_STEPS = ['Submitted', 'In review', 'Done'];

function FeedbackItemThread({ record }) {
  const { user } = useCurrentUser();
  const [open, setOpen] = useState(record.status === 'needs_more_info');
  const [thread, setThread] = useState(null);
  const [threadStatus, setThreadStatus] = useState('idle');
  const [reply, setReply] = useState('');
  const [sending, setSending] = useState(false);
  // True between a committed send and the next successful thread reload, so a
  // failed post-send refresh shows a truthful stale-data warning (AR2-m01).
  const [awaitingPostSendRefresh, setAwaitingPostSendRefresh] = useState(false);

  const needsInfo = record.status === 'needs_more_info';
  const closed = TERMINAL_RECORD_STATUSES.has(record.status);
  const trimmedReply = reply.trim();
  const replyOverLimit = trimmedReply.length > FEEDBACK_COMMENT_MAX_LENGTH;

  const loadThread = useCallback(async () => {
    setThreadStatus('loading');
    try {
      const response = await getGovernedFeedback(record.id);
      setThread(response?.data?.comments || []);
      setThreadStatus('ready');
      setAwaitingPostSendRefresh(false);
    } catch {
      // Keep any previously loaded thread rendered — a reload failure right
      // after a successful reply must not make the reply appear lost.
      setThreadStatus('error');
    }
  }, [record.id]);

  useEffect(() => {
    if (open && thread === null && threadStatus === 'idle') {
      loadThread();
    }
  }, [loadThread, open, thread, threadStatus]);

  async function sendReply() {
    const body = reply.trim();
    // Match the server's shared comment bound so oversize text is refused here
    // instead of costing the reporter a round trip and a lost draft.
    if (!body || body.length > FEEDBACK_COMMENT_MAX_LENGTH) return;
    // One idempotency key per logical send, held in session storage keyed by
    // reporter + record + body signature (mirrors the queue cleanup flow): a
    // retry after an ambiguous failure (network/timeout/5xx) reuses the SAME
    // key so the server replays the original comment instead of duplicating it.
    const mutationIdentity = {
      actorUserId: user?.id,
      operation: 'feedback-comment',
      resourceId: record.id,
    };
    const pending = pendingFeedbackMutation({
      ...mutationIdentity,
      signature: feedbackMutationSignature({ feedbackId: record.id, body }),
    });
    setSending(true);
    try {
      const response = await commentOnGovernedFeedback(record.id, body, {
        idempotencyKey: pending.key,
      });
      // Committed: append the SERVER-RETURNED comment locally first (deduped by
      // id), so the reply stays visible exactly once even when the immediate
      // thread reload fails (AR2-m01). Then drop the retained key and the sent
      // text, and reload best-effort — loadThread keeps the appended thread on
      // failure, so a reload failure can never surface as "reply failed".
      const committed = response?.data;
      if (committed?.id) {
        setThread((current) =>
          (current || []).some((comment) => comment.id === committed.id)
            ? current
            : [...(current || []), committed],
        );
        setThreadStatus('ready');
        setAwaitingPostSendRefresh(true);
      }
      clearPendingFeedbackMutation(mutationIdentity);
      setReply('');
      await loadThread();
    } catch (error) {
      // Keep the key (and the typed reply) only for ambiguous failures so the
      // retry replays; a terminal validation/conflict result clears it.
      if (!shouldRetainFeedbackMutation(error)) clearPendingFeedbackMutation(mutationIdentity);
      toast.error({
        title: 'Reply failed',
        body: error?.message || 'Could not send your reply.',
      });
    } finally {
      setSending(false);
    }
  }

  return (
    <div className={`feedback-mine-thread ${needsInfo ? 'needs-info' : ''}`.trim()}>
      <button
        type="button"
        className="feedback-mine-thread-toggle"
        data-testid={`feedback-mine-thread-toggle-${record.id}`}
        aria-expanded={open}
        onClick={() =>
          setOpen((value) => {
            // Reopening after a failed load retries instead of staying dead.
            if (!value && threadStatus === 'error') setThreadStatus('idle');
            return !value;
          })
        }
      >
        {needsInfo
          ? open
            ? 'The team asked for more info — reply below'
            : 'The team asked for more info — reply'
          : open
            ? 'Hide replies'
            : 'Replies'}
      </button>
      {open ? (
        <div
          className="feedback-mine-thread-body"
          data-testid={`feedback-mine-thread-${record.id}`}
        >
          {threadStatus === 'loading' ? <span className="feedback-mine-meta">Loading…</span> : null}
          {threadStatus === 'error' ? (
            (thread || []).length ? (
              // Stale-data warning, separate from send success: the committed
              // reply below stays visible; only the refresh failed.
              <span className="feedback-mine-meta" role="status">
                {awaitingPostSendRefresh
                  ? "Your reply was sent, but the latest replies couldn't be refreshed."
                  : "The latest replies couldn't be refreshed — showing the last loaded replies."}{' '}
                <button
                  type="button"
                  className="btn ghost sm"
                  data-testid={`feedback-mine-thread-refresh-${record.id}`}
                  onClick={loadThread}
                >
                  Refresh now
                </button>
              </span>
            ) : (
              <span className="feedback-mine-meta">
                Replies are unavailable right now.{' '}
                <button type="button" className="btn ghost sm" onClick={loadThread}>
                  Retry
                </button>
              </span>
            )
          ) : null}
          {(thread || []).map((comment) => (
            <div
              key={comment.id}
              className={`feedback-mine-comment ${comment.authorRole === 'reporter' ? 'mine' : 'team'}`}
            >
              <span className="feedback-mine-comment-author">
                {comment.authorRole === 'reporter' ? 'You' : 'PRIME product team'}
                {comment.createdAt ? (
                  <span className="feedback-mine-meta"> · {formatWhen(comment.createdAt)}</span>
                ) : null}
              </span>
              <span>{comment.body}</span>
            </div>
          ))}
          {threadStatus === 'ready' && (thread || []).length === 0 ? (
            <span className="feedback-mine-meta">No replies yet.</span>
          ) : null}
          {closed ? (
            <span className="feedback-mine-meta">This request is closed.</span>
          ) : (
            <div className="feedback-mine-reply">
              <textarea
                data-testid={`feedback-mine-reply-input-${record.id}`}
                aria-label="Reply to the product team"
                placeholder={
                  needsInfo
                    ? 'Add the detail the team asked for…'
                    : 'Add a comment for the product team…'
                }
                value={reply}
                onChange={(event) => setReply(event.target.value)}
                maxLength={FEEDBACK_COMMENT_MAX_LENGTH}
                aria-invalid={replyOverLimit || undefined}
                rows={2}
              />
              {reply.length > FEEDBACK_COMMENT_MAX_LENGTH - 200 ? (
                <span
                  className={`feedback-mine-meta${replyOverLimit ? ' feedback-error-text' : ''}`}
                  data-testid={`feedback-mine-reply-count-${record.id}`}
                >
                  {replyOverLimit
                    ? `${trimmedReply.length - FEEDBACK_COMMENT_MAX_LENGTH} characters over the ${FEEDBACK_COMMENT_MAX_LENGTH} limit`
                    : `${FEEDBACK_COMMENT_MAX_LENGTH - reply.length} characters left`}
                </span>
              ) : null}
              <button
                type="button"
                className="btn primary sm"
                data-testid={`feedback-mine-reply-send-${record.id}`}
                disabled={sending || !trimmedReply || replyOverLimit}
                onClick={sendReply}
              >
                {sending ? 'Sending…' : 'Send reply'}
              </button>
            </div>
          )}
        </div>
      ) : null}
    </div>
  );
}

function PackageTimeline({ stage }) {
  return (
    <div className="feedback-mine-timeline" data-testid="feedback-mine-timeline">
      {TIMELINE_STEPS.map((step, index) => (
        <span
          key={step}
          aria-current={index === stage ? 'step' : undefined}
          className={`feedback-mine-timeline-step ${index <= stage ? 'hit' : ''} ${
            index === stage ? 'now' : ''
          } ${stage === 2 ? 'done' : ''}`.trim()}
        >
          <span className="feedback-mine-timeline-dot" aria-hidden />
          {step}
          {index === stage ? <span className="sr-only"> (current)</span> : null}
        </span>
      ))}
    </div>
  );
}

function defaultViewFromCounts(counts, { hasActiveSession = false } = {}) {
  // TFB-03 (Benji, live 2026-07-23): an active session means a draft exists
  // RIGHT NOW even when the loaded counts predate it — exiting the editor must
  // land on Drafts, or the fresh work looks lost.
  if (hasActiveSession) return 'drafts';
  if ((counts?.draft || 0) > 0) return 'drafts';
  if ((counts?.submitted || 0) > 0) return 'submitted';
  if ((counts?.resolved || 0) > 0) return 'resolved';
  // A brand-new tester with nothing anywhere starts on Drafts (its empty state
  // explains how to capture) — never on an empty Resolved tab.
  return 'drafts';
}

function lifecycleCountsFromState(state) {
  return state.historyMeta?.lifecycleCounts || EMPTY_LIFECYCLE_COUNTS;
}

function tabLabel(view, counts) {
  const count =
    view === 'drafts'
      ? counts.draft || 0
      : view === 'submitted'
        ? counts.submitted || 0
        : counts.resolved || 0;
  return `${view.charAt(0).toUpperCase()}${view.slice(1)} (${count})`;
}

function draftChipForPackage(pkg, activePackage, activeStage) {
  if (pkg?.recoveryKind === 'server_missing') {
    return { label: 'Recovered local draft', tone: 'amber' };
  }
  const hasFailedSave =
    pkg?.localOnly ||
    (pkg?.id === activePackage?.id &&
      (activeStage?.saveStatus === 'save_failed' ||
        activePackage?.screens?.some((screen) => screen?.saveStatus === 'save_failed')));
  return hasFailedSave
    ? { label: 'Not saved to server', tone: 'amber' }
    : { label: 'Draft', tone: 'slate' };
}

function packageMatchesSearch(pkg, query) {
  const normalizedQuery = query.toLowerCase();
  if (!normalizedQuery) return true;
  const routes = [
    pkg?.primaryRoute,
    pkg?.route,
    ...(pkg?.screens || []).map((screen) => screen?.route),
  ];
  return [pkg?.title, ...routes].some((value) =>
    String(value || '')
      .toLowerCase()
      .includes(normalizedQuery),
  );
}

function ViewFeedbackButton({ packageId, onOpen }) {
  return (
    <button
      type="button"
      className="btn ghost sm"
      data-testid={`feedback-view-${packageId}`}
      onClick={() => onOpen(packageId)}
    >
      View feedback
    </button>
  );
}

function packageScreenRoute(screen) {
  const metadata = screen?.metadata || {};
  const route = metadata.route || screen?.route || '';
  return `${route}${metadata.query || ''}${metadata.hash || ''}`;
}

function packageScreenTitle(screen, index) {
  return (
    screen?.metadata?.title ||
    screen?.metadata?.pageTitle ||
    screen?.title ||
    `Captured screen ${index + 1}`
  );
}

function packageScreenAnnotations(screen) {
  return screen?.metadata?.annotations || screen?.annotations || [];
}

function sentenceCaseStatus(value, fallback) {
  const normalized = String(value || fallback || '')
    .replaceAll('_', ' ')
    .trim();
  return normalized ? `${normalized.charAt(0).toUpperCase()}${normalized.slice(1)}` : '';
}

function DraftPackageDetail({ pkg, detail, onResume, onOpenScreen }) {
  const screens = detail?.screens || [];
  const firstSource = screens.map(packageScreenRoute).find(Boolean);
  return (
    <section
      id={`feedback-package-detail-${pkg.id}`}
      className="feedback-package-detail"
      data-testid={`feedback-package-detail-${pkg.id}`}
      aria-label={`Details for ${pkg.title || 'feedback draft'}`}
    >
      <div className="feedback-package-detail-status">
        <span>
          <strong>Package status</strong>{' '}
          {sentenceCaseStatus(detail?.status || pkg.status, 'draft')}
        </span>
        <span>
          <strong>Save state</strong>{' '}
          {sentenceCaseStatus(detail?.saveStatus || pkg.saveStatus, 'saved')}
        </span>
        {firstSource ? (
          <Link className="btn ghost sm" to={firstSource}>
            Return to {firstSource}
          </Link>
        ) : null}
      </div>
      <ol className="feedback-package-screen-list">
        {screens.map((screen, index) => {
          const annotations = packageScreenAnnotations(screen);
          const route = packageScreenRoute(screen);
          const title = packageScreenTitle(screen, index);
          const id = screen?.screenId || screen?.id || index;
          return (
            <li className="feedback-package-screen" key={id}>
              <div className="feedback-package-screen-head">
                <div>
                  <span className="feedback-package-screen-number">Screen {index + 1}</span>
                  <strong>{title}</strong>
                  {route ? <code>{route}</code> : null}
                </div>
                <button
                  type="button"
                  className="btn ghost sm"
                  aria-label={`Open screen ${index + 1} in editor`}
                  onClick={() => (onOpenScreen ? onOpenScreen(screen) : onResume())}
                >
                  Open in editor
                </button>
              </div>
              <div className="feedback-mine-meta">
                {annotations.length} mark{annotations.length === 1 ? '' : 's'} ·{' '}
                {sentenceCaseStatus(screen?.saveStatus, 'saved')}
              </div>
              {annotations.length ? (
                <ul className="feedback-package-mark-list">
                  {annotations.map((annotation, annotationIndex) => (
                    <li key={annotation?.id || annotationIndex}>
                      {annotation?.instruction ||
                        annotation?.text ||
                        sentenceCaseStatus(annotation?.type, `Mark ${annotationIndex + 1}`)}
                    </li>
                  ))}
                </ul>
              ) : null}
            </li>
          );
        })}
      </ol>
    </section>
  );
}

const SEARCH_DEBOUNCE_MS = 300;

export function MyFeedbackPage() {
  const {
    state,
    deleteDraftPackage,
    loadHistory,
    openPackage,
    openFeedback,
    openEndSession,
    endFeedbackSession,
    openScreenForEditing,
  } = useFeedbackMarkup();
  const navigate = useNavigate();
  const { roles, user } = useCurrentUser();
  const [searchParams, setSearchParams] = useSearchParams();
  // BL-06: arriving from the submit confirmation briefly highlights the
  // Submitted tab and the newest package so nobody hunts through Drafts.
  const location = useLocation();
  const [arrivalHighlight, setArrivalHighlight] = useState(
    Boolean(location.state?.highlightSubmitted),
  );
  useEffect(() => {
    if (!arrivalHighlight) return undefined;
    const timer = setTimeout(() => setArrivalHighlight(false), 6000);
    return () => clearTimeout(timer);
  }, [arrivalHighlight]);
  const [records, setRecords] = useState([]);
  const [recordsStatus, setRecordsStatus] = useState('loading');
  // True while the current view was chosen by the default heuristic rather
  // than an explicit user click — only auto-chosen views may be upgraded.
  const autoViewRef = useRef(false);
  const [searchText, setSearchText] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteStatus, setDeleteStatus] = useState('idle');
  const [deleteError, setDeleteError] = useState('');
  const [expandedPackageId, setExpandedPackageId] = useState(null);
  const [packageDetails, setPackageDetails] = useState({});
  const packageDetailRequestsRef = useRef(new Map());
  const packageDetailIdentityEpochRef = useRef(0);
  const actorUserId = user?.id || null;
  // Typed confirmation ("DELETE") — the only delete path in the product, so a
  // fat-fingered click can never destroy a tester's work.
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const deleteDialogRef = useRef(null);
  const deleteCancelRef = useRef(null);
  const deleteOpenerRef = useRef(null);

  const canSeeQueue = rbac.hasAnyRole(roles, rbac.ROUTE_PERMISSIONS['/feedback/queue'] || []);
  // Both filters live in the query string so a filtered view is linkable and
  // survives a reload — the same seam the lifecycle tab already uses.
  const requestedTypeFilter = searchParams.get('type') || TYPE_FILTER_ALL;
  const typeFilter = TYPE_FILTER_OPTIONS.some((option) => option.value === requestedTypeFilter)
    ? requestedTypeFilter
    : TYPE_FILTER_ALL;
  const requestedStateFilter = searchParams.get('state') || '';
  const stateFilter = MY_FEEDBACK_STATES.includes(requestedStateFilter) ? requestedStateFilter : '';
  // A typed-only filter hides the package sections entirely; 'screen_feedback'
  // hides the typed section. Both visible by default.
  const showPackages = typeFilter === TYPE_FILTER_ALL || typeFilter === SCREEN_FEEDBACK_TYPE;
  const showIntakes = typeFilter !== SCREEN_FEEDBACK_TYPE;
  // 'deflected' is a state only a question can be in, so it excludes packages.
  const packagesInState = !stateFilter || PACKAGE_STATES.includes(stateFilter);
  const packageSectionVisible = showPackages && packagesInState;
  const requestedView = searchParams.get('view');
  const requestedValidView = VALID_VIEWS.has(requestedView) ? requestedView : null;
  // STATE DERIVES THE VIEW (audit P2-5). Both parameters are linkable, so a
  // bookmarked, hand-built, or pasted URL can carry two that disagree —
  // `?state=drafts&view=resolved`, or a `?state=resolved` with no view at all,
  // which would let the auto-default pick a different tab. Either one renders the
  // package tab in one state while the typed section below is filtered to
  // another, which defeats the whole point of the shared vocabulary.
  //
  // `state` wins because it is the page-wide control the reporter set, and the
  // in-app handlers already move the two together (`selectStateFilter`). A state
  // with no package equivalent ('deflected') derives nothing — the package
  // sections are hidden for it, so there is nothing left to disagree with.
  const viewFromState = stateFilter ? STATE_TO_VIEW[stateFilter] || null : null;
  const selectedView = viewFromState || requestedValidView;
  const serverLifecycleCounts = lifecycleCountsFromState(state);
  const activePackage = state.package;
  const recordRequestIdRef = useRef(0);
  // Merge ONLY local-only work with content. A server-backed draft is already
  // represented in the server counts even when the current lifecycle filter
  // hides it from historyItems, and an empty launcher package has nothing to
  // lose or resume.
  const activePackageIsMerged = Boolean(
    activePackage &&
    activePackage.localOnly &&
    activePackage.status !== 'submitted' &&
    !activePackage.submittedAt &&
    (activePackage.screens?.length || 0) > 0 &&
    !(state.historyItems || []).some((item) => item?.id === activePackage?.id),
  );
  const trimmedSearch = searchText.trim();
  const mergedPackageVisible =
    activePackageIsMerged && packageMatchesSearch(activePackage, trimmedSearch);
  const lifecycleCounts = useMemo(
    () => ({
      ...serverLifecycleCounts,
      draft: (serverLifecycleCounts.draft || 0) + (mergedPackageVisible ? 1 : 0),
    }),
    [mergedPackageVisible, serverLifecycleCounts],
  );
  const hasActiveSession = Boolean(
    activePackage && activePackage.status !== 'submitted' && !activePackage.submittedAt,
  );
  const activeView = selectedView || defaultViewFromCounts(lifecycleCounts, { hasActiveSession });
  const deleting = deleteStatus === 'deleting';

  useEffect(() => {
    packageDetailIdentityEpochRef.current += 1;
    packageDetailRequestsRef.current.clear();
    setPackageDetails({});
    setExpandedPackageId(null);
  }, [actorUserId]);

  const loadPackageDetail = useCallback(async (pkg, useLocalScreens = false) => {
    if (!pkg?.id) return null;
    if (useLocalScreens && Array.isArray(pkg.screens)) {
      setPackageDetails((current) => ({
        ...current,
        [pkg.id]: { status: 'ready', data: pkg },
      }));
      return pkg;
    }
    if (packageDetailRequestsRef.current.has(pkg.id)) {
      return packageDetailRequestsRef.current.get(pkg.id);
    }

    let request;
    const identityEpoch = packageDetailIdentityEpochRef.current;
    setPackageDetails((current) => {
      if (current[pkg.id]) return current;
      return { ...current, [pkg.id]: { status: 'loading', data: null } };
    });
    request = getFeedbackPackage(pkg.id)
      .then((response) => {
        const data = response?.data || null;
        if (identityEpoch !== packageDetailIdentityEpochRef.current) return null;
        setPackageDetails((current) => ({
          ...current,
          [pkg.id]: { status: 'ready', data },
        }));
        return data;
      })
      .catch(() => {
        if (identityEpoch !== packageDetailIdentityEpochRef.current) return null;
        setPackageDetails((current) => ({
          ...current,
          [pkg.id]: { status: 'error', data: null },
        }));
        return null;
      });
    packageDetailRequestsRef.current.set(pkg.id, request);
    return request;
  }, []);

  const historyPackageIds = useMemo(
    () => (state.historyItems || []).map((item) => item?.id).filter(Boolean),
    [state.historyItems],
  );

  const refreshRecords = useCallback(async (packageIds = []) => {
    const requestId = recordRequestIdRef.current + 1;
    recordRequestIdRef.current = requestId;
    try {
      if (!packageIds.length) {
        setRecords([]);
        setRecordsStatus('ready');
        return;
      }
      // The API caps sourcePackageIds at 100 per request — an active tester
      // who loads three history pages must not blank every status chip with a
      // deterministic 400. Batch and merge instead.
      const loaded = [];
      for (let start = 0; start < packageIds.length; start += 100) {
        const batch = packageIds.slice(start, start + 100);
        let cursor = null;
        do {
          const response = await listGovernedFeedback({
            sourcePackageIds: batch.join(','),
            limit: 50,
            ...(cursor ? { cursor } : {}),
          });
          if (requestId !== recordRequestIdRef.current) return;
          loaded.push(...(Array.isArray(response?.data) ? response.data : []));
          cursor = response?.page?.nextCursor || null;
        } while (cursor);
      }
      if (requestId !== recordRequestIdRef.current) return;
      setRecords(
        loaded.filter(
          (item, index, all) => all.findIndex((candidate) => candidate?.id === item?.id) === index,
        ),
      );
      setRecordsStatus('ready');
    } catch {
      if (requestId !== recordRequestIdRef.current) return;
      setRecordsStatus('error');
    }
  }, []);

  useEffect(() => {
    const handle = setTimeout(() => setDebouncedSearch(trimmedSearch), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(handle);
  }, [trimmedSearch]);

  const refreshHistory = useCallback(async () => {
    const query = debouncedSearch ? { q: debouncedSearch } : {};
    if (!selectedView) {
      try {
        await loadHistory(query);
      } catch {
        // The provider already records the page error state.
      }
      return;
    }
    try {
      await loadHistory({
        ...query,
        lifecycle: VIEW_TO_LIFECYCLE[selectedView],
      });
    } catch {
      // The provider already records the page error state.
    }
  }, [loadHistory, selectedView, debouncedSearch]);

  useEffect(() => {
    refreshHistory();
  }, [refreshHistory]);

  // Governed records are limited to visible, authorized package IDs and every
  // required cursor page is traversed so lifecycle chips never infer state
  // from a partial first page.
  useEffect(() => {
    refreshRecords(historyPackageIds);
  }, [historyPackageIds, refreshRecords]);

  // Normalize the URL to the view that actually rendered, with `replace` so the
  // conflicting entry does not stay in the history stack: a back/forward step or
  // a copied link must not be able to reintroduce the disagreement (audit P2-5).
  useEffect(() => {
    if (!viewFromState || requestedView === viewFromState) return;
    setSearchParams(
      (current) => {
        const next = new URLSearchParams(current);
        next.set('view', viewFromState);
        return next;
      },
      { replace: true },
    );
  }, [requestedView, setSearchParams, viewFromState]);

  useEffect(() => {
    if (selectedView || state.historyStatus !== 'loaded') return;
    autoViewRef.current = true;
    // TFB-03: the loaded counts may predate a just-captured draft (the Benji
    // sequence: counts loaded at menu-open, capture, Done) — an active session
    // always defaults to Drafts so fresh work is never hidden behind an empty
    // Resolved tab.
    const nextView = defaultViewFromCounts(lifecycleCounts, { hasActiveSession });
    setSearchParams(
      (current) => {
        const next = new URLSearchParams(current);
        next.set('view', nextView);
        return next;
      },
      { replace: true },
    );
  }, [hasActiveSession, lifecycleCounts, selectedView, setSearchParams, state.historyStatus]);

  // If the default landed the tester on Drafts while a reviewer question is
  // waiting on a submitted record, move to the Submitted tab — the amber
  // "Needs more info" chip must not hide behind a tab switch. Only an
  // auto-chosen view is upgraded; an explicit user choice is respected. An
  // active local-only draft is more urgent because it exists only on this
  // device, so keep it visible until the tester explicitly changes tabs.
  useEffect(() => {
    if (!autoViewRef.current || recordsStatus !== 'ready') return;
    if (activePackageIsMerged) return;
    if (activeView === 'submitted') return;
    if (records.some((record) => record.status === 'needs_more_info')) {
      autoViewRef.current = false;
      setSearchParams(
        (current) => {
          const next = new URLSearchParams(current);
          next.set('view', 'submitted');
          return next;
        },
        { replace: true },
      );
    }
  }, [activePackageIsMerged, activeView, records, recordsStatus, setSearchParams]);

  const recordsByPackage = useMemo(() => {
    const groups = new Map();
    for (const record of records) {
      const key = record.sourcePackageId || 'unlinked';
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key).push(record);
    }
    return groups;
  }, [records]);

  const visiblePackages = useMemo(() => {
    if (!activeView) return [];
    const lifecycle = VIEW_TO_LIFECYCLE[activeView];
    const packages = Array.isArray(state.historyItems) ? state.historyItems : [];
    const withActivePackage = activePackageIsMerged ? [activePackage, ...packages] : packages;
    return withActivePackage.filter((pkg) => {
      if (activePackageIsMerged && pkg.id === activePackage.id) {
        return activeView === 'drafts' && packageMatchesSearch(pkg, trimmedSearch);
      }
      return pkg.lifecycle === lifecycle;
    });
  }, [activePackage, activePackageIsMerged, activeView, state.historyItems, trimmedSearch]);

  const loading = state.historyStatus === 'loading';
  const loadingMore = state.historyStatus === 'loading-more';
  const loadError = state.historyStatus === 'error';
  const empty = !loading && !loadError && visiblePackages.length === 0;

  function selectView(view) {
    setSearchParams((current) => {
      const next = new URLSearchParams(current);
      next.set('view', view);
      // Once the reporter has opted into state filtering, the tabs and the state
      // filter move together — otherwise the page would show the Submitted tab
      // while the typed section below still showed drafts. With no state filter
      // active (the default) a tab click changes nothing else, which is exactly
      // the pre-commit-6 behaviour.
      if (next.get('state')) next.set('state', view);
      return next;
    });
  }

  function selectTypeFilter(nextType) {
    setSearchParams((current) => {
      const next = new URLSearchParams(current);
      if (nextType) next.set('type', nextType);
      else next.delete('type');
      return next;
    });
  }

  function selectStateFilter(nextState) {
    setSearchParams((current) => {
      const next = new URLSearchParams(current);
      if (nextState) next.set('state', nextState);
      else next.delete('state');
      // A package state also selects the matching lifecycle tab, so the two
      // controls can never disagree about what the page is showing.
      if (STATE_TO_VIEW[nextState]) next.set('view', STATE_TO_VIEW[nextState]);
      return next;
    });
  }

  // ONE setSearchParams call, deliberately. Two consecutive calls in the same
  // handler both read the same searchParams snapshot, so the second silently
  // discards the first — clearing the type and then the state would have left the
  // type filter in place.
  function clearUnifiedFilters() {
    setSearchParams((current) => {
      const next = new URLSearchParams(current);
      next.delete('type');
      next.delete('state');
      return next;
    });
  }

  function onTablistKeyDown(event) {
    const currentIndex = LIFECYCLE_VIEWS.indexOf(activeView);
    let nextIndex = null;
    if (event.key === 'ArrowRight') nextIndex = (currentIndex + 1) % LIFECYCLE_VIEWS.length;
    else if (event.key === 'ArrowLeft')
      nextIndex = (currentIndex + LIFECYCLE_VIEWS.length - 1) % LIFECYCLE_VIEWS.length;
    else if (event.key === 'Home') nextIndex = 0;
    else if (event.key === 'End') nextIndex = LIFECYCLE_VIEWS.length - 1;
    if (nextIndex === null) return;
    event.preventDefault();
    const view = LIFECYCLE_VIEWS[nextIndex];
    selectView(view);
    document.querySelector(`[data-testid="feedback-tab-${view}"]`)?.focus();
  }

  useEffect(() => {
    if (deleteTarget) deleteCancelRef.current?.focus();
  }, [deleteTarget]);

  function closeDeleteDialog({ restoreFocus = true } = {}) {
    setDeleteTarget(null);
    setDeleteStatus('idle');
    setDeleteError('');
    setDeleteConfirmText('');
    const opener = deleteOpenerRef.current;
    deleteOpenerRef.current = null;
    if (restoreFocus && opener && document.contains(opener)) {
      opener.focus();
    } else if (restoreFocus) {
      document.querySelector(`[data-testid="feedback-tab-${activeView || 'drafts'}"]`)?.focus();
    }
  }

  function onDeleteDialogKeyDown(event) {
    if (event.key === 'Escape') {
      event.preventDefault();
      if (!deleting) closeDeleteDialog();
      return;
    }
    if (event.key !== 'Tab') return;
    const focusables = Array.from(
      deleteDialogRef.current?.querySelectorAll('button:not([disabled]), input:not([disabled])') ||
        [],
    );
    if (!focusables.length) {
      // Both actions are disabled while deleting; keep focus inside the modal.
      event.preventDefault();
      deleteDialogRef.current?.focus();
      return;
    }
    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  const deleteConfirmed = deleteConfirmText.trim() === 'DELETE';

  async function confirmDeleteDraft() {
    if (!deleteTarget || deleting || !deleteConfirmed) return;
    setDeleteStatus('deleting');
    setDeleteError('');
    // Disabling both actions drops focus to <body>, escaping the dialog's
    // keydown trap; hold focus on the dialog container instead.
    deleteDialogRef.current?.focus();
    try {
      await deleteDraftPackage(deleteTarget.id);
      // The opener button disappears with the deleted card, so skip the
      // opener-restore path and land focus on the active tab after refresh.
      closeDeleteDialog({ restoreFocus: false });
      await refreshHistory();
      document.querySelector(`[data-testid="feedback-tab-${activeView || 'drafts'}"]`)?.focus();
    } catch (error) {
      setDeleteStatus('failed');
      setDeleteError(error?.message || 'Could not delete this draft right now.');
    }
  }

  async function loadMore() {
    const cursor = state.historyMeta?.nextCursor;
    if (!cursor || loadingMore) return;
    const query = {
      cursor,
      ...(debouncedSearch ? { q: debouncedSearch } : {}),
      ...(selectedView ? { lifecycle: VIEW_TO_LIFECYCLE[selectedView] } : {}),
    };
    try {
      await loadHistory(query, { append: true });
    } catch {
      // The provider retains already-loaded rows and exposes a retryable error.
    }
  }

  return (
    <section className="feedback-mine-page fbmk-redesign" data-testid="feedback-history">
      <header className="feedback-history-header">
        <div>
          <div className="eyebrow">Feedback</div>
          <h1>My Feedback</h1>
          <p>Every request you send is tracked here from Submitted through Done.</p>
        </div>
        <button
          type="button"
          className="btn primary"
          data-testid="feedback-mine-give-feedback"
          onClick={openFeedback}
        >
          New feedback
        </button>
      </header>

      {/* Unified filters (commit 6). One control pair over both sources; both
          default to "everything", so an unfiltered page is byte-for-byte the page
          that shipped before this commit. */}
      <div className="feedback-mine-filters" data-testid="feedback-mine-filters">
        <label className="feedback-mine-filter">
          <span>Type</span>
          <select
            data-testid="feedback-mine-filter-type"
            value={typeFilter}
            onChange={(event) => selectTypeFilter(event.target.value)}
          >
            {TYPE_FILTER_OPTIONS.map((option) => (
              <option key={option.value || 'all'} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </label>
        <label className="feedback-mine-filter">
          <span>State</span>
          <select
            data-testid="feedback-mine-filter-state"
            value={stateFilter}
            onChange={(event) => selectStateFilter(event.target.value)}
          >
            {STATE_FILTER_OPTIONS.map((option) => (
              <option key={option.value || 'any'} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </label>
        {typeFilter || stateFilter ? (
          <button
            type="button"
            className="btn ghost sm"
            data-testid="feedback-mine-filter-clear"
            onClick={clearUnifiedFilters}
          >
            Show everything
          </button>
        ) : null}
        {/* Said plainly rather than left as an empty section: a reporter who
            filters to "Answered by PRIME" must know screen feedback was excluded
            because it cannot be in that state, not because they have none. */}
        {showPackages && !packagesInState ? (
          <span
            className="feedback-mine-meta"
            data-testid="feedback-mine-filter-note"
            role="status"
          >
            Screen feedback is never answered by PRIME, so none of it appears in this state.
          </span>
        ) : null}
      </div>

      {packageSectionVisible ? (
        <div className="feedback-mine-toolbar">
          <div
            className="feedback-mine-tabs"
            role="tablist"
            aria-label="Feedback lifecycle views"
            onKeyDown={onTablistKeyDown}
          >
            {LIFECYCLE_VIEWS.map((view) => (
              <button
                key={view}
                type="button"
                role="tab"
                id={`feedback-tab-btn-${view}`}
                data-testid={`feedback-tab-${view}`}
                aria-selected={activeView === view ? 'true' : 'false'}
                aria-controls={
                  /* must reference an element that exists; panels render only
                   for the loaded selected view */
                  activeView === view && selectedView && !loading && !loadError
                    ? `feedback-tabpanel-${VIEW_TO_LIFECYCLE[view]}`
                    : undefined
                }
                tabIndex={activeView === view ? 0 : -1}
                className={`btn ${activeView === view ? 'primary' : 'ghost'} sm${
                  arrivalHighlight && view === 'submitted' ? ' is-arrival-highlight' : ''
                }`}
                onClick={() => selectView(view)}
              >
                {tabLabel(view, lifecycleCounts)}
              </button>
            ))}
          </div>

          <label className="feedback-mine-search">
            <span className="sr-only">Search your feedback</span>
            <input
              data-testid="feedback-history-search"
              type="search"
              value={searchText}
              onChange={(event) => setSearchText(event.target.value)}
              placeholder="Search your feedback"
            />
          </label>
        </div>
      ) : null}

      {packageSectionVisible && loading ? (
        <div className="fbmk-state">
          <TableSkeleton rows={4} />
        </div>
      ) : null}

      {packageSectionVisible && loadError ? (
        <div className="fbmk-state" data-testid="feedback-history-error">
          <BrandedEmptyState
            icon="⚠"
            eyebrow="Feedback"
            tone="brick"
            title="Could not load your feedback."
            description={state.historyError || 'The feedback service did not respond. Try again.'}
            action={
              <button type="button" className="btn ghost sm" onClick={() => void refreshHistory()}>
                Retry
              </button>
            }
          />
        </div>
      ) : null}

      {packageSectionVisible && empty ? (
        <div data-testid="feedback-history-empty">
          <BrandedEmptyState
            icon="✎"
            eyebrow="Feedback"
            title={
              {
                drafts: 'No drafts right now.',
                submitted: 'No submitted feedback yet.',
                resolved: 'Nothing resolved yet.',
              }[activeView] || 'No feedback right now.'
            }
            description={
              trimmedSearch
                ? 'Try a different search.'
                : 'Use Feedback in the top bar — one click captures the screen you are on, and your requests are tracked here.'
            }
            tone="slate"
          />
        </div>
      ) : null}

      {packageSectionVisible && !loading && !loadError && selectedView === 'drafts' ? (
        <div
          className="feedback-mine-section"
          id="feedback-tabpanel-draft"
          role="tabpanel"
          aria-labelledby="feedback-tab-btn-drafts"
          data-testid="feedback-tabpanel-draft"
        >
          <div className="feedback-mine-list">
            {visiblePackages.map((pkg) => {
              const screenCount = pkg.screenCount || pkg.screens?.length || 0;
              // The list summary lags the live session (a freshly captured
              // stage is not mirrored until its first save); the shared
              // session counter is the truth for the ACTIVE package.
              const effectiveScreenCount =
                activePackage?.id === pkg.id ? countFeedbackScreens(state) : screenCount;
              const annotationCount = pkg.annotationCount || 0;
              const isMergedActivePackage = activePackageIsMerged && pkg.id === activePackage?.id;
              const isLocalOnlyMergedPackage = isMergedActivePackage && pkg.localOnly;
              // The provider's live package is the freshest screen truth for the
              // ACTIVE session (server-backed or local-only): it includes
              // save_failed local screens the server detail cannot know about.
              const useProviderScreens =
                activePackage?.id === pkg.id && (activePackage.screens?.length || 0) > 0;
              const detailSourcePkg = useProviderScreens ? activePackage : pkg;
              const chip = draftChipForPackage(pkg, activePackage, state.stage);
              const detailEntry = packageDetails[pkg.id];
              const detailStatus = detailEntry?.status || 'idle';
              const detail = useProviderScreens ? activePackage : detailEntry?.data;
              const isExpanded = expandedPackageId === pkg.id;
              const resumeScreenTarget = () => {
                const screens = activePackage?.screens || [];
                const wanted = activePackage?.resumeContext?.screenId;
                if (wanted && screens.some((s) => (s.screenId || s.id) === wanted)) return wanted;
                const last = screens[screens.length - 1];
                return last?.screenId || last?.id || null;
              };
              const resumePackage = () => {
                if (isLocalOnlyMergedPackage) {
                  // A local-only session has no /:id/edit deep link; reopen its
                  // stage directly (openScreenForEditing navigates itself).
                  const target = resumeScreenTarget();
                  if (target) {
                    void openScreenForEditing(target);
                  } else {
                    navigate('/feedback/workspace');
                  }
                  return;
                }
                openPackage(pkg.id, {
                  openWorkspace: true,
                  mode: 'edit',
                });
              };
              const openScreenFromDetail = (screen) => {
                const screenId = screen?.screenId || screen?.id;
                if (screenId && activePackage?.id === pkg.id) {
                  void openScreenForEditing(screenId);
                  return;
                }
                resumePackage();
              };
              return (
                <article
                  className="feedback-mine-card"
                  key={pkg.id}
                  data-package-testid={`feedback-package-${pkg.id}`}
                  data-testid="feedback-history-row"
                >
                  <div className="feedback-mine-card-head">
                    <span className={`feedback-chip ${chip.tone}`}>{chip.label}</span>
                    <strong>{pkg.title || 'Feedback draft'}</strong>
                    <span className="feedback-mine-when">{formatWhen(pkg.updatedAt)}</span>
                    <button
                      type="button"
                      className="feedback-package-detail-toggle"
                      data-testid={`feedback-details-toggle-${pkg.id}`}
                      aria-expanded={isExpanded}
                      aria-controls={isExpanded ? `feedback-package-detail-${pkg.id}` : undefined}
                      aria-label={`${isExpanded ? 'Hide' : 'Show'} details for ${
                        pkg.title || 'feedback draft'
                      }`}
                      onClick={() => {
                        const nextExpanded = !isExpanded;
                        setExpandedPackageId(nextExpanded ? pkg.id : null);
                        if (nextExpanded) {
                          void loadPackageDetail(detailSourcePkg, useProviderScreens);
                        }
                      }}
                    >
                      <span aria-hidden="true">⌄</span>
                    </button>
                  </div>
                  <div className="feedback-mine-meta">
                    {/* The item's TYPE, from the shared chokepoint, so a package
                        row and a typed row on the same page name themselves the
                        same way (commit 6). */}
                    {feedbackEntryTypePluralLabel(SCREEN_FEEDBACK_TYPE)} · {effectiveScreenCount}{' '}
                    screen{effectiveScreenCount === 1 ? '' : 's'} · {annotationCount} mark
                    {annotationCount === 1 ? '' : 's'}
                    {pkg.primaryRoute ? ` · captured on ${pkg.primaryRoute}` : ''}
                  </div>
                  <div className="feedback-mine-actions">
                    <button
                      type="button"
                      className="btn primary sm"
                      data-testid={`feedback-resume-${pkg.id}`}
                      onClick={resumePackage}
                    >
                      Resume
                    </button>
                    {!isLocalOnlyMergedPackage ? (
                      <button
                        type="button"
                        className="btn ghost sm"
                        data-testid={`feedback-review-package-${pkg.id}`}
                        disabled={effectiveScreenCount === 0}
                        onClick={() =>
                          openPackage(pkg.id, {
                            openReview: true,
                            mode: 'edit',
                          })
                        }
                      >
                        Review
                      </button>
                    ) : null}
                    {isLocalOnlyMergedPackage ? (
                      <button
                        type="button"
                        className="btn ghost sm"
                        data-testid={`feedback-save-new-${pkg.id}`}
                        onClick={() => void endFeedbackSession({ disposition: 'save' })}
                      >
                        Save as new draft
                      </button>
                    ) : null}
                    {isLocalOnlyMergedPackage ? (
                      <button
                        type="button"
                        className="btn ghost danger sm"
                        data-testid={`feedback-discard-${pkg.id}`}
                        onClick={openEndSession}
                      >
                        Discard draft
                      </button>
                    ) : null}
                    {!isLocalOnlyMergedPackage ? (
                      <button
                        type="button"
                        className="btn ghost danger sm"
                        data-testid={`feedback-delete-${pkg.id}`}
                        onClick={(event) => {
                          deleteOpenerRef.current = event.currentTarget;
                          setDeleteTarget(pkg);
                          setDeleteStatus('idle');
                          setDeleteError('');
                        }}
                      >
                        Delete draft
                      </button>
                    ) : null}
                  </div>
                  {screenCount > 0 ? (
                    <FeedbackThumbStrip
                      packageId={pkg.id}
                      screens={(useProviderScreens ? activePackage.screens : detail?.screens) || []}
                      status={useProviderScreens ? 'ready' : detailStatus}
                      onIntent={() => void loadPackageDetail(detailSourcePkg, useProviderScreens)}
                      onActivate={() => {
                        setExpandedPackageId(pkg.id);
                        void loadPackageDetail(detailSourcePkg, useProviderScreens);
                      }}
                    />
                  ) : null}
                  {isExpanded ? (
                    (useProviderScreens || (detailStatus === 'ready' && detail)) && detail ? (
                      <DraftPackageDetail
                        pkg={pkg}
                        detail={detail}
                        onResume={resumePackage}
                        onOpenScreen={openScreenFromDetail}
                      />
                    ) : (
                      <div
                        id={`feedback-package-detail-${pkg.id}`}
                        className="feedback-package-detail is-loading"
                        aria-live="polite"
                      >
                        {detailStatus === 'error'
                          ? 'Screen details are unavailable right now.'
                          : 'Loading screen details…'}
                      </div>
                    )
                  ) : null}
                </article>
              );
            })}
          </div>
        </div>
      ) : null}

      {packageSectionVisible &&
      !loading &&
      !loadError &&
      (selectedView === 'submitted' || selectedView === 'resolved') ? (
        <div
          className="feedback-mine-section"
          id={`feedback-tabpanel-${VIEW_TO_LIFECYCLE[selectedView]}`}
          role="tabpanel"
          aria-labelledby={`feedback-tab-btn-${selectedView}`}
          data-testid={`feedback-tabpanel-${VIEW_TO_LIFECYCLE[selectedView]}`}
        >
          <div className="feedback-mine-list">
            {visiblePackages.map((pkg) => {
              const pkgRecords = recordsByPackage.get(pkg.id) || [];
              const stage = packageTimelineStageFromSummary(pkg.governedRecordSummary, pkgRecords);
              const lifecycleLabel =
                selectedView === 'resolved' ? 'Resolved' : TIMELINE_STEPS[stage];
              const lifecycleTone =
                selectedView === 'resolved' ? 'green' : stage === 2 ? 'green' : 'blue';
              const submittedFeedbackCount =
                Number(
                  pkg.governedRecordSummary?.linkedCount ?? pkg.governedRecordSummary?.total,
                ) ||
                pkgRecords.length ||
                pkg.submittedFeedbackIds?.length ||
                pkg.submittedFeedbackCount ||
                0;

              return (
                <article
                  className={`feedback-mine-card${
                    arrivalHighlight && selectedView === 'submitted' && pkg === visiblePackages[0]
                      ? ' is-arrival-highlight'
                      : ''
                  }`}
                  key={pkg.id}
                  data-package-testid={`feedback-package-${pkg.id}`}
                  data-testid="feedback-history-row"
                >
                  <div className="feedback-mine-card-head">
                    <span
                      className={`feedback-chip ${lifecycleTone}`}
                      data-testid="feedback-mine-package-status"
                    >
                      {lifecycleLabel}
                    </span>
                    <strong>{pkg.title || pkg.id}</strong>
                    <span className="feedback-mine-when">
                      {formatWhen(pkg.submittedAt || pkg.updatedAt)}
                    </span>
                  </div>
                  <div className="feedback-mine-meta">
                    {/* Type and application area, so a package row reads like the
                        typed-intake rows below it and like the reviewer's queue
                        card (commit 6). */}
                    {feedbackEntryTypePluralLabel(SCREEN_FEEDBACK_TYPE)} · {submittedFeedbackCount}{' '}
                    request{submittedFeedbackCount === 1 ? '' : 's'}
                    {pkg.primaryRoute ? ` · captured on ${pkg.primaryRoute}` : ''}
                  </div>
                  <PackageTimeline stage={stage} />
                  {pkgRecords.length > 0 ? (
                    <ol className="feedback-mine-items">
                      {pkgRecords.map((record, index) => {
                        const chip = recordChip(record);
                        return (
                          <li key={record.id} data-testid="feedback-mine-item">
                            <div className="feedback-mine-item-row">
                              <span className="feedback-mine-item-index">
                                {String(index + 1).padStart(2, '0')}
                              </span>
                              <span className="feedback-mine-item-title">{record.title}</span>
                              {/* BL-17: keep the governed FDBK reference visible
                                  after the confirmation page is gone. */}
                              <code
                                className="feedback-mine-item-ref"
                                data-testid={`feedback-mine-item-ref-${record.id}`}
                              >
                                {record.id}
                              </code>
                              <span
                                className={`feedback-chip ${chip.tone} feedback-mine-item-chip`}
                                data-testid={`feedback-mine-item-chip-${record.id}`}
                              >
                                {chip.label}
                              </span>
                            </div>
                            <FeedbackItemThread record={record} />
                          </li>
                        );
                      })}
                    </ol>
                  ) : recordsStatus === 'error' ? (
                    <div className="feedback-mine-meta">
                      Request statuses are unavailable right now.
                    </div>
                  ) : null}

                  <div className="feedback-mine-actions">
                    <ViewFeedbackButton
                      packageId={pkg.id}
                      onOpen={(packageId) =>
                        openPackage(packageId, {
                          openWorkspace: true,
                          mode: 'view',
                        })
                      }
                    />
                    {canSeeQueue ? (
                      <Link
                        className="btn ghost sm"
                        data-testid={`feedback-history-triage-link-${pkg.id}`}
                        to="/feedback/queue"
                      >
                        View in triage queue
                      </Link>
                    ) : null}
                  </div>
                </article>
              );
            })}
          </div>
        </div>
      ) : null}

      {packageSectionVisible && !loading && !loadError && state.historyMeta?.hasMore ? (
        <div className="feedback-mine-load-more">
          <span className="feedback-mine-meta">
            {(state.historyItems || []).length} shown of {state.historyMeta.total}
          </span>
          {state.historyAppendError ? (
            <span className="feedback-error-text" data-testid="feedback-history-append-error">
              {state.historyAppendError}
            </span>
          ) : null}
          <button
            type="button"
            className="btn ghost sm"
            onClick={() => void loadMore()}
            disabled={loadingMore}
          >
            {loadingMore ? 'Loading…' : 'Load more feedback'}
          </button>
        </div>
      ) : null}

      {/* Typed help/intake framework. Still a SEPARATE section rather than a
          fourth lifecycle tab: the tabs above derive their counts from screenshot
          packages and their linked governed records, and folding a second
          paginated source into those counts would mis-state them (architecture
          review §8.16). Commit 6 unifies the two through the FILTERS instead —
          one type control and one state control that both sources answer to —
          which gives a coherent page without redefining what a package tab means.
          Every label here comes from the server or a shared chokepoint, so it can
          never claim a ticket was delivered. */}
      {showIntakes ? (
        <MySupportTicketsSection
          typeFilter={typeFilter === SCREEN_FEEDBACK_TYPE ? '' : typeFilter}
          stateFilter={stateFilter}
        />
      ) : null}

      {deleteTarget ? (
        <div className="feedback-modal-backdrop" role="presentation">
          <section
            ref={deleteDialogRef}
            tabIndex={-1}
            className="feedback-confirm-dialog"
            role="dialog"
            aria-modal="true"
            aria-busy={deleting}
            aria-label="Delete this draft?"
            data-testid="feedback-delete-dialog"
            onKeyDown={onDeleteDialogKeyDown}
          >
            <div className="feedback-panel-kicker">Delete draft</div>
            <h3>Delete this draft?</h3>
            <p>
              This permanently deletes &apos;{deleteTarget.title}&apos;, including{' '}
              {deleteTarget.screenCount || 0} screenshot
              {(deleteTarget.screenCount || 0) === 1 ? '' : 's'} and{' '}
              {deleteTarget.annotationCount || 0} mark
              {(deleteTarget.annotationCount || 0) === 1 ? '' : 's'}. Submitted feedback is not
              affected.
            </p>
            <label
              className="feedback-delete-confirm-label"
              htmlFor="feedback-delete-confirm-input"
            >
              Type <strong>DELETE</strong> to confirm
            </label>
            <input
              id="feedback-delete-confirm-input"
              type="text"
              className="feedback-delete-confirm-input"
              data-testid="feedback-delete-confirm-input"
              value={deleteConfirmText}
              disabled={deleting}
              autoComplete="off"
              spellCheck={false}
              aria-describedby="feedback-delete-confirm-help"
              onChange={(event) => setDeleteConfirmText(event.target.value)}
            />
            <p
              id="feedback-delete-confirm-help"
              className="feedback-delete-confirm-help"
              data-testid="feedback-delete-confirm-help"
            >
              {deleteConfirmed
                ? 'Confirmed — Delete draft is now enabled.'
                : 'The Delete button stays disabled until you type DELETE exactly (uppercase).'}
            </p>
            {deleteError ? (
              <p className="feedback-error-text" data-testid="feedback-delete-error">
                {deleteError}
              </p>
            ) : null}
            <div className="feedback-confirm-actions">
              <button
                type="button"
                ref={deleteCancelRef}
                className="btn ghost"
                data-testid="feedback-delete-cancel"
                disabled={deleting}
                onClick={() => closeDeleteDialog()}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn danger"
                data-testid="feedback-delete-confirm"
                disabled={deleting || !deleteConfirmed}
                onClick={() => void confirmDeleteDraft()}
              >
                {deleting ? 'Deleting…' : 'Delete draft'}
              </button>
            </div>
          </section>
        </div>
      ) : null}
    </section>
  );
}
